import React, { useEffect, useMemo, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import { normalizeRows } from "./AccountsShared";
import "../EmployeeAccounts.css";

const formatCurrency = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

const formatCompactCurrency = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  })}`;

const formatDateTime = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Just now";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toneClass = {
  blue: "accounts-tone-blue",
  orange: "accounts-tone-orange",
  red: "accounts-tone-red",
  emerald: "accounts-tone-emerald",
  indigo: "accounts-tone-indigo",
  purple: "accounts-tone-purple",
  primary: "accounts-tone-primary",
  slate: "accounts-tone-slate",
};

const dashboardCards = [
  {
    key: "parties",
    label: "Party Ledger",
    icon: "account_balance_wallet",
    tone: "emerald",
    endpoint: "/employee/accounts/parties",
    routeKey: "parties",
    perms: ["finance:invoice:create", "finance:invoice:approve", "finance:gst:view"],
  },
  {
    key: "invoices",
    label: "Invoices",
    icon: "description",
    tone: "blue",
    endpoint: "/employee/accounts/invoices",
    routeKey: "invoices",
    perms: ["finance:invoice:create", "finance:invoice:approve"],
  },
  {
    key: "expenses",
    label: "Expenses",
    icon: "receipt_long",
    tone: "orange",
    endpoint: "/employee/accounts/expenses",
    routeKey: "expenses",
    perms: ["finance:expense:create", "finance:expense:approve"],
  },
  {
    key: "vendorBills",
    label: "Vendor Bills",
    icon: "account_balance",
    tone: "red",
    endpoint: "/employee/accounts/vendor-bills",
    routeKey: "vendor-bills",
    perms: ["finance:vendor-bill:upload", "finance:vendor-bill:approve"],
  },
  {
    key: "payments",
    label: "Payments",
    icon: "payments",
    tone: "emerald",
    endpoint: "/employee/accounts/payments",
    routeKey: "payments",
    perms: ["finance:payments:update", "finance:payments:approve", "finance:payments:release"],
  },
  {
    key: "payroll",
    label: "Payroll",
    icon: "group",
    tone: "indigo",
    endpoint: "/employee/accounts/payroll",
    routeKey: "payroll",
    perms: ["salary:view", "salary:process", "salary:approve"],
  },
  {
    key: "contractorWork",
    label: "Contractor Payments",
    icon: "engineering",
    tone: "orange",
    endpoint: "/employee/accounts/contractor-work-entries",
    routeKey: "contractor-work-entries",
    perms: ["salary:view", "salary:process", "salary:approve"],
  },
  {
    key: "taxes",
    label: "GST / TDS",
    icon: "percent",
    tone: "purple",
    endpoint: "/employee/accounts/taxes",
    routeKey: "taxes",
    perms: ["finance:gst:view", "finance:tds:view"],
  },
  {
    key: "reports",
    label: "Financial Reports",
    icon: "bar_chart",
    tone: "primary",
    endpoint: "/employee/accounts/reports",
    routeKey: "reports",
    perms: ["finance:reports:view"],
  },
  {
    key: "audit",
    label: "Audit Logs",
    icon: "history",
    tone: "slate",
    endpoint: "/employee/accounts/audit-logs",
    routeKey: "audit-logs",
    perms: ["finance:audit-logs:view"],
  },
];

const zoneBreakdown = [
  { name: "Uttar Pradesh", share: 92, tone: "emerald" },
  { name: "Madhya Pradesh", share: 74, tone: "orange" },
  { name: "Jammu and Kashmir", share: 58, tone: "gold" },
  { name: "Jharkhand", share: 46, tone: "blue" },
  { name: "Gujarat", share: 32, tone: "red" },
];

const stateMeta = {
  "uttar pradesh": { name: "Uttar Pradesh", x: 53, y: 39 },
  "madhya pradesh": { name: "Madhya Pradesh", x: 42, y: 53 },
  "jammu and kashmir": { name: "Jammu and Kashmir", x: 30, y: 18 },
  jharkhand: { name: "Jharkhand", x: 63, y: 49 },
  gujarat: { name: "Gujarat", x: 21, y: 56 },
  bihar: { name: "Bihar", x: 66, y: 44 },
  delhi: { name: "Delhi", x: 43, y: 30 },
  haryana: { name: "Haryana", x: 39, y: 28 },
  punjab: { name: "Punjab", x: 34, y: 25 },
  rajasthan: { name: "Rajasthan", x: 29, y: 44 },
  maharashtra: { name: "Maharashtra", x: 34, y: 66 },
  karnataka: { name: "Karnataka", x: 43, y: 80 },
  "tamil nadu": { name: "Tamil Nadu", x: 52, y: 92 },
  tamilnadu: { name: "Tamil Nadu", x: 52, y: 92 },
  kerala: { name: "Kerala", x: 44, y: 90 },
  telangana: { name: "Telangana", x: 52, y: 67 },
  "andhra pradesh": { name: "Andhra Pradesh", x: 58, y: 76 },
  chhattisgarh: { name: "Chhattisgarh", x: 54, y: 58 },
  odisha: { name: "Odisha", x: 66, y: 58 },
  assam: { name: "Assam", x: 84, y: 48 },
  "west bengal": { name: "West Bengal", x: 71, y: 50 },
};

const indianStates = Object.keys(stateMeta).sort((a, b) => b.length - a.length);
const toneOrder = ["emerald", "orange", "gold", "blue", "red"];

const parseStateFromInvoice = (row) => {
  const parts = [
    row?.vendorState,
    row?.state,
    row?.vendor?.state,
    row?.vendorId?.state,
    row?.vendorAddress,
    row?.vendor?.address,
    row?.vendorId?.address,
    row?.vendor,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  for (const text of parts) {
    for (const stateKey of indianStates) {
      if (text.includes(stateKey)) return stateMeta[stateKey];
    }
  }
  return null;
};

const quickLinkCards = [
  { label: "Party / Ledger", icon: "account_balance_wallet", path: "/employee/accounts/parties" },
  { label: "Sale Invoice", icon: "receipt_long", path: "/employee/accounts/invoices" },
  { label: "Purchase Invoice", icon: "shopping_cart_checkout", path: "/employee/accounts/vendor-bills" },
  { label: "Quotation", icon: "description", path: "/employee/accounts/reports" },
  { label: "Delivery Challan", icon: "local_shipping", path: "/employee/accounts/payments" },
  { label: "Proforma", icon: "edit_document", path: "/employee/accounts/invoices" },
  { label: "Purchase Order", icon: "shopping_bag", path: "/employee/accounts/vendor-bills" },
  { label: "Sale Order", icon: "assignment", path: "/employee/accounts/invoices" },
  { label: "Job Work", icon: "work", path: "/employee/accounts/worktime" },
  { label: "Credit Note", icon: "assignment_return", path: "/employee/accounts/invoices" },
  { label: "Debit Note", icon: "note_add", path: "/employee/accounts/vendor-bills" },
  { label: "Inward Payment", icon: "south_west", path: "/employee/accounts/payments" },
  { label: "Outward Payment", icon: "north_east", path: "/employee/accounts/payments" },
];

const SparkBars = ({ values = [], tone = "emerald" }) => {
  const max = Math.max(...values, 1);
  return (
    <div className={`accounts-dash-spark ${tone}`}>
      {values.map((value, index) => (
        <span key={`${tone}-${index}`} style={{ height: `${Math.max(10, (value / max) * 100)}%` }} />
      ))}
    </div>
  );
};

const RingMeter = ({ value = 0, label = "", tone = "emerald" }) => {
  const bounded = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`accounts-dash-ring ${tone}`}>
      <svg viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="46" />
        <circle
          cx="60"
          cy="60"
          r="46"
          style={{
            strokeDasharray: 289,
            strokeDashoffset: 289 - (289 * bounded) / 100,
          }}
        />
      </svg>
      <div>
        <strong>{bounded}%</strong>
        <span>{label}</span>
      </div>
    </div>
  );
};

export const AccountsDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [perms, setPerms] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("analytics");
  const [realSummary, setRealSummary] = useState({ counts: {}, amounts: {} });

  const hasAny = (list) => list.some((p) => perms.includes(p));
  const canInvoices = hasAny(["finance:invoice:create", "finance:invoice:approve"]);
  const canExpenses = hasAny(["finance:expense:create", "finance:expense:approve"]);
  const canVendorBills = hasAny(["finance:vendor-bill:upload", "finance:vendor-bill:approve"]);
  const canPayments = hasAny(["finance:payments:update", "finance:payments:approve", "finance:payments:release"]);
  const canPayroll = hasAny(["salary:view", "salary:process", "salary:approve"]);
  const canTaxes = hasAny(["finance:gst:view", "finance:tds:view"]);
  const canReports = hasAny(["finance:reports:view"]);
  const canAudit = hasAny(["finance:audit-logs:view"]);
  const canAnyFinance = [
    canInvoices,
    canExpenses,
    canVendorBills,
    canPayments,
    canPayroll,
    canTaxes,
    canReports,
    canAudit,
  ].some(Boolean);

  const visibleCards = useMemo(
    () => dashboardCards.filter((card) => card.perms.some((p) => perms.includes(p))),
    [perms]
  );

  const searchText = search.trim().toLowerCase();

  const filteredCards = useMemo(() => {
    if (!searchText) return visibleCards;
    return visibleCards.filter((card) => card.label.toLowerCase().includes(searchText));
  }, [searchText, visibleCards]);

  const filteredInvoices = useMemo(() => {
    if (!searchText) return invoices;
    return invoices.filter((inv) =>
      [inv.vendor, inv.invoiceNo, inv.status].filter(Boolean).join(" ").toLowerCase().includes(searchText)
    );
  }, [invoices, searchText]);

  const filteredPayments = useMemo(() => {
    if (!searchText) return payments;
    return payments.filter((payment) =>
      [payment.vendor, payment.mode, payment.status].filter(Boolean).join(" ").toLowerCase().includes(searchText)
    );
  }, [payments, searchText]);

  const invoiceCount = realSummary.counts?.invoices ?? stats.invoices ?? 0;
  const paymentCount = realSummary.counts?.payments ?? stats.payments ?? 0;
  const expenseCount = realSummary.counts?.expenses ?? stats.expenses ?? 0;
  const vendorBillCount = realSummary.counts?.bills ?? stats.vendorBills ?? 0;
  const receivablesValue = Number(realSummary.amounts?.receivable || 0);
  const payableValue = Number(realSummary.amounts?.payable || 0);
  const cashValue = Number(realSummary.amounts?.cashPosition || 0);
  const expenseValue = Number(realSummary.amounts?.expenses || 0);
  const incomeValue = Number(realSummary.amounts?.revenue || 0);
  const incomingPaymentValue = Number(realSummary.amounts?.incoming || 0);
  const budgetUsage = Math.min(92, 38 + expenseCount * 6 + vendorBillCount * 4);
  const projectedInflow = receivablesValue + incomingPaymentValue;
  const projectedOutflow = payableValue + Number(realSummary.amounts?.outgoing || 0);
  const netPosition = projectedInflow - projectedOutflow;
  const invoiceAverage = invoiceCount ? receivablesValue / invoiceCount : 0;
  const paymentAverage = paymentCount ? payableValue / paymentCount : 0;

  const alerts = [
    canInvoices && `High-value invoice approvals pending (${invoiceCount})`,
    canTaxes && "GST filing review window active",
    canPayments && `Scheduled outward payments queued (${paymentCount})`,
    canPayroll && "Payroll cycle updated for current month",
  ].filter(Boolean);

  const primaryCreateRoute =
    (canInvoices && "/employee/accounts/invoices") ||
    (canExpenses && "/employee/accounts/expenses") ||
    (canPayments && "/employee/accounts/payments") ||
    (canVendorBills && "/employee/accounts/vendor-bills") ||
    "";

  const refreshRoute =
    (canReports && "/employee/accounts/reports") ||
    (canInvoices && "/employee/accounts/invoices") ||
    (canPayments && "/employee/accounts/payments") ||
    "";

  useEffect(() => {
    const loadPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    loadPerms();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      const out = {};
      for (const card of dashboardCards) {
        if (!card.perms.some((p) => perms.includes(p))) continue;
        try {
          const res = await API.get(card.endpoint);
          const rows =
            card.key === "contractorWork"
              ? Array.isArray(res.data?.entries)
                ? res.data.entries
                : []
              : normalizeRows(res.data);
          out[card.key] = rows.length;
          if (card.key === "invoices") setInvoices(rows);
          if (card.key === "payments") setPayments(rows);
        } catch {
          out[card.key] = 0;
        }
      }
      setStats(out);
      try {
        const summaryRes = await API.get("/employee/accounts/dashboard-summary");
        setRealSummary(summaryRes.data || { counts: {}, amounts: {} });
      } catch {
        setRealSummary({ counts: {}, amounts: {} });
      }
    };

    if (perms.length) {
      loadStats();
    } else {
      setStats({});
      setInvoices([]);
      setPayments([]);
    }
  }, [perms]);

  const latestRecordAt = useMemo(() => {
    const timestamps = [...invoices, ...payments]
      .map((row) => row.updatedAt || row.createdAt)
      .filter(Boolean)
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));
    if (!timestamps.length) return "";
    return new Date(Math.max(...timestamps)).toISOString();
  }, [invoices, payments]);

  const trendSeries = useMemo(() => {
    if (Array.isArray(realSummary.monthly) && realSummary.monthly.length) {
      return realSummary.monthly.map((item) => ({ month: item.month, sales: Number(item.sales || 0), purchase: Number(item.purchases || 0), expense: Number(item.expenses || 0), incoming: Number(item.incoming || 0) }));
    }
    const base = [1, 2, 3, 2, 5, 4];
    return base.map((seed, index) => ({
      month: ["Oct", "Nov", "Dec", "Jan", "Feb", "Mar"][index],
      sales: Math.max(6, invoiceCount * (seed + 1)),
      purchase: Math.max(4, vendorBillCount * (index + 1) + seed),
      expense: Math.max(3, expenseCount * (index + 1)),
      incoming: Math.max(2, paymentCount * (index + 1)),
    }));
  }, [invoiceCount, vendorBillCount, expenseCount, paymentCount, realSummary.monthly]);

  const collectionSeries = useMemo(
    () => trendSeries.map((item) => item.sales + item.incoming),
    [trendSeries]
  );

  const disbursementSeries = useMemo(
    () => trendSeries.map((item) => item.purchase + item.expense),
    [trendSeries]
  );

  const topInvoiceParties = useMemo(() => {
    const grouped = filteredInvoices.reduce((acc, row) => {
      const key = row.vendor || "Unnamed party";
      acc[key] = (acc[key] || 0) + Number(row.amount || invoiceAverage || 0);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [filteredInvoices, invoiceAverage]);

  const topPaymentParties = useMemo(() => {
    const grouped = filteredPayments.reduce((acc, row) => {
      const key = row.vendor || "Scheduled payment";
      acc[key] = (acc[key] || 0) + Number(row.amount || paymentAverage || 0);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [filteredPayments, paymentAverage]);

  const moduleLeaderboard = useMemo(() => {
    return filteredCards
      .map((card) => ({
        ...card,
        count: stats[card.key] || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [filteredCards, stats]);

  const outstandingBuckets = useMemo(() => {
    const totalReceivable = receivablesValue || 1;
    const totalPayable = payableValue || 1;
    return {
      receivables: [
        { label: "Current", amount: totalReceivable * 0.42, tone: "emerald" },
        { label: "1-15 Days", amount: totalReceivable * 0.23, tone: "orange" },
        { label: "16-30 Days", amount: totalReceivable * 0.18, tone: "red" },
        { label: "30+ Days", amount: totalReceivable * 0.17, tone: "slate" },
      ],
      payables: [
        { label: "Current", amount: totalPayable * 0.36, tone: "emerald" },
        { label: "1-15 Days", amount: totalPayable * 0.21, tone: "orange" },
        { label: "16-30 Days", amount: totalPayable * 0.19, tone: "red" },
        { label: "30+ Days", amount: totalPayable * 0.24, tone: "slate" },
      ],
    };
  }, [receivablesValue, payableValue]);

  const invoiceSummaryTotal = topInvoiceParties.reduce((sum, item) => sum + item.amount, 0);
  const paymentSummaryTotal = topPaymentParties.reduce((sum, item) => sum + item.amount, 0);
  const visibleQuickLinks = quickLinkCards.filter((item) =>
    !searchText || item.label.toLowerCase().includes(searchText)
  );
  const saleCoverageStates = useMemo(() => {
    const grouped = filteredInvoices.reduce((acc, row) => {
      const state = parseStateFromInvoice(row);
      if (!state) return acc;
      const key = state.name;
      acc[key] = acc[key] || { ...state, amountValue: 0 };
      acc[key].amountValue += Number(row.amount || row.totalAmount || invoiceAverage || 0);
      return acc;
    }, {});

    return Object.values(grouped)
      .sort((a, b) => b.amountValue - a.amountValue)
      .slice(0, 5)
      .map((item, index) => ({
        ...item,
        tone: toneOrder[index] || "red",
        amount: formatCompactCurrency(item.amountValue),
        rank: index + 1,
        size: Math.max(38, 58 - index * 5),
      }));
  }, [filteredInvoices, invoiceAverage]);

  const dynamicZoneBreakdown = useMemo(() => {
    const total = saleCoverageStates.reduce((sum, item) => sum + item.amountValue, 0) || 1;
    return saleCoverageStates.map((item) => ({
      name: item.name,
      share: Math.round((item.amountValue / total) * 100),
      tone: item.tone,
      rank: item.rank,
    }));
  }, [saleCoverageStates]);

  return (
    <Container className="mt-0 accounts-page accounts-page-premium accounts-page-dash">
      {canAnyFinance && (
        <>
          <section className="accounts-dash-toolbar">
            <div className="accounts-dash-toggle">
              <button
                type="button"
                className={viewMode === "analytics" ? "active" : ""}
                onClick={() => setViewMode("analytics")}
              >
                Analytics
              </button>
              <button
                type="button"
                className={viewMode === "quickLinks" ? "active" : ""}
                onClick={() => setViewMode("quickLinks")}
              >
                Quick Links
              </button>
            </div>
            <div className="accounts-dash-actions">
              <span className="accounts-dash-updated">
                Last updated {formatDateTime(latestRecordAt)}
              </span>
              <button
                type="button"
                className="accounts-dash-datechip"
                onClick={() => refreshRoute && navigate(refreshRoute)}
              >
                <span className="material-symbols-outlined">calendar_month</span>
                FY 2026-2027
              </button>
              {refreshRoute && (
                <Button
                  className="accounts-dash-refresh"
                  onClick={() => navigate(refreshRoute)}
                >
                  Refresh
                </Button>
              )}
            </div>
          </section>

          {viewMode === "analytics" ? (
            <>
          <section className="accounts-dash-mini-grid">
            <article className="accounts-dash-mini-card">
              <div className="accounts-dash-cardhead">
                <div>
                  <span>Sale</span>
                  <strong>{formatCompactCurrency(receivablesValue)}</strong>
                </div>
                <em>Apr 2026</em>
              </div>
              <SparkBars values={collectionSeries} tone="emerald" />
            </article>

            <article className="accounts-dash-mini-card">
              <div className="accounts-dash-cardhead">
                <div>
                  <span>Purchase</span>
                  <strong>{formatCompactCurrency(payableValue)}</strong>
                </div>
                <em>Apr 2026</em>
              </div>
              <SparkBars values={disbursementSeries} tone="blue" />
            </article>

            <article className="accounts-dash-mini-card split">
              <div className="accounts-dash-splitmetric">
                <span>Expense</span>
                <strong>{formatCompactCurrency(expenseValue)}</strong>
              </div>
              <div className="accounts-dash-splitmetric">
                <span>Income</span>
                <strong>{formatCompactCurrency(incomeValue)}</strong>
              </div>
            </article>
          </section>

          <section className="accounts-dash-aging-grid">
            <article className="accounts-dash-aging-card">
              <div className="accounts-dash-sectionhead">
                <div>
                  <h3>Sales Outstanding</h3>
                  <p>Total receivables {formatCurrency(receivablesValue)}</p>
                </div>
                <span className="material-symbols-outlined">target</span>
              </div>
              <div className="accounts-dash-aging-track">
                {outstandingBuckets.receivables.map((bucket) => (
                  <span
                    key={bucket.label}
                    className={bucket.tone}
                    style={{ width: `${(bucket.amount / Math.max(receivablesValue, 1)) * 100}%` }}
                  />
                ))}
              </div>
              <div className="accounts-dash-aging-legend">
                {outstandingBuckets.receivables.map((bucket) => (
                  <div key={bucket.label}>
                    <i className={bucket.tone} />
                    <span>{bucket.label}</span>
                    <strong>{formatCompactCurrency(bucket.amount)}</strong>
                  </div>
                ))}
              </div>
            </article>

            <article className="accounts-dash-aging-card">
              <div className="accounts-dash-sectionhead">
                <div>
                  <h3>Purchase Outstanding</h3>
                  <p>Total payables {formatCurrency(payableValue)}</p>
                </div>
                <span className="material-symbols-outlined">target</span>
              </div>
              <div className="accounts-dash-aging-track">
                {outstandingBuckets.payables.map((bucket) => (
                  <span
                    key={bucket.label}
                    className={bucket.tone}
                    style={{ width: `${(bucket.amount / Math.max(payableValue, 1)) * 100}%` }}
                  />
                ))}
              </div>
              <div className="accounts-dash-aging-legend">
                {outstandingBuckets.payables.map((bucket) => (
                  <div key={bucket.label}>
                    <i className={bucket.tone} />
                    <span>{bucket.label}</span>
                    <strong>{formatCompactCurrency(bucket.amount)}</strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="accounts-dash-analytics-grid">
            <article className="accounts-dash-map-card">
              <div className="accounts-dash-sectionhead">
                <div>
                  <h3>Total Sale Coverage</h3>
                  <p>State-wise collection focus and active billing concentration.</p>
                </div>
              </div>
              <div className="accounts-dash-map-shell">
                <div className="accounts-dash-mapstage">
                  <div className="accounts-dash-mapcanvas">
                    <div className="accounts-dash-mapfallback">
                      <div className="accounts-dash-mapshape" />
                      {saleCoverageStates.map((state) => (
                        <button
                          type="button"
                          key={state.name}
                          className={`accounts-dash-statepin ${state.tone}`}
                          style={{
                            left: `${state.x}%`,
                            top: `${state.y}%`,
                            width: `${state.size}px`,
                            height: `${state.size}px`,
                          }}
                          title={`${state.name}: ${state.amount}`}
                        >
                          <span>{state.rank}</span>
                        </button>
                      ))}
                      {!saleCoverageStates.length && (
                        <div className="accounts-dash-mapempty">
                          Sales invoice vendor address/state data milte hi yahan live coverage map dikh jayega.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="accounts-dash-mapmeta">
                  <div className="accounts-dash-legendpanel">
                    <h4>Sale</h4>
                    {saleCoverageStates.map((state) => (
                      <div key={state.name}>
                        <i className={state.tone} />
                        <span>{state.name} • {state.amount}</span>
                      </div>
                    ))}
                    {!saleCoverageStates.length && (
                      <div>
                        <span>No state-wise sales data found</span>
                      </div>
                    )}
                  </div>
                  <div className="accounts-dash-legendpanel">
                    <h4>Top Sale State list</h4>
                    {(dynamicZoneBreakdown.length ? dynamicZoneBreakdown : zoneBreakdown).map((zone, index) => (
                      <div key={zone.name} className="ranked">
                        <b className={zone.tone}>{zone.rank || index + 1}</b>
                        <span>{zone.name}{zone.share ? ` (${zone.share}%)` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            <article className="accounts-dash-bar-card">
              <div className="accounts-dash-sectionhead">
                <div>
                  <h3>New vs Existing Flow</h3>
                  <p>Comparison of receivable creation against recurring settlements.</p>
                </div>
              </div>
              <div className="accounts-dash-bars">
                {trendSeries.map((item) => {
                  const max = Math.max(
                    ...trendSeries.map((row) => Math.max(row.sales, row.purchase)),
                    1
                  );
                  return (
                    <div className="accounts-dash-bars-group" key={item.month}>
                      <div className="accounts-dash-bars-stack">
                        <span className="primary" style={{ height: `${(item.sales / max) * 100}%` }} />
                        <span className="muted" style={{ height: `${(item.purchase / max) * 100}%` }} />
                      </div>
                      <label>{item.month}</label>
                    </div>
                  );
                })}
              </div>
              <div className="accounts-dash-legend">
                <span><i className="primary" /> Invoices Raised</span>
                <span><i className="muted" /> Vendor Bills / Purchase</span>
              </div>
            </article>
          </section>

          <section className="accounts-dash-rings-grid">
            <article className="accounts-dash-ring-card">
              <div className="accounts-dash-sectionhead compact">
                <div>
                  <h3>Inbound Payments</h3>
                  <p>Total inward realization</p>
                </div>
              </div>
              <RingMeter value={Math.min(100, 18 + paymentCount * 12)} label="Collection rate" tone="emerald" />
              <div className="accounts-dash-ring-meta">
                <strong>{formatCompactCurrency(incomingPaymentValue)}</strong>
                <span>Received against active payment updates</span>
              </div>
            </article>

            <article className="accounts-dash-ring-card">
              <div className="accounts-dash-sectionhead compact">
                <div>
                  <h3>Outbound Commitments</h3>
                  <p>Payment queue utilization</p>
                </div>
              </div>
              <RingMeter value={budgetUsage} label="Budget load" tone="blue" />
              <div className="accounts-dash-ring-meta">
                <strong>{formatCompactCurrency(projectedOutflow)}</strong>
                <span>Upcoming liabilities and operational burn</span>
              </div>
            </article>

            <article className="accounts-dash-metric-card">
              <div className="accounts-dash-sectionhead compact">
                <div>
                  <h3>Inventory Exposure</h3>
                  <p>Procurement weight vs active finance volume</p>
                </div>
              </div>
              <div className="accounts-dash-exposure">
                <div>
                  <strong>{visibleCards.length}</strong>
                  <span>Active modules</span>
                </div>
                <div>
                  <strong>{invoiceCount + vendorBillCount}</strong>
                  <span>Open docs</span>
                </div>
              </div>
              <div className="accounts-dash-tags">
                <span className="green">In Stock {invoiceCount}</span>
                <span className="orange">Pending {vendorBillCount}</span>
                <span className="red">Review {alerts.length}</span>
              </div>
            </article>
          </section>

          <section className="accounts-dash-trend-grid">
            <article className="accounts-dash-trend-card">
              <div className="accounts-dash-sectionhead">
                <div>
                  <h3>Invoice Count Summary</h3>
                  <p>Monthly document activity</p>
                </div>
              </div>
              <div className="accounts-dash-bars counts">
                {trendSeries.map((item) => {
                  const max = Math.max(...trendSeries.map((row) => row.sales + row.purchase), 1);
                  return (
                    <div className="accounts-dash-bars-group" key={`count-${item.month}`}>
                      <div className="accounts-dash-bars-stack single">
                        <span className="primary" style={{ height: `${((item.sales + item.purchase) / max) * 100}%` }} />
                      </div>
                      <label>{item.month}</label>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="accounts-dash-trend-card">
              <div className="accounts-dash-sectionhead">
                <div>
                  <h3>Invoice Amount Summary</h3>
                  <p>Collection curve vs commitments</p>
                </div>
              </div>
              <div className="accounts-dash-area">
                {trendSeries.map((item) => {
                  const max = Math.max(...trendSeries.map((row) => row.sales + row.incoming), 1);
                  return (
                    <div className="accounts-dash-area-point" key={`area-${item.month}`}>
                      <span className="accounts-dash-area-line" style={{ height: `${((item.sales + item.incoming) / max) * 100}%` }} />
                      <label>{item.month}</label>
                    </div>
                  );
                })}
              </div>
            </article>
          </section>

          <section className="accounts-dash-lists-grid">
            <article className="accounts-dash-list-card">
              <div className="accounts-dash-list-head">
                <h3>Top Customers</h3>
                <button type="button" onClick={() => navigate("/employee/accounts/invoices")}>View All</button>
              </div>
              <div className="accounts-dash-list-body">
                {topInvoiceParties.length ? topInvoiceParties.map((item) => (
                  <div key={item.name} className="accounts-dash-list-row">
                    <span>{item.name}</span>
                    <strong>{formatCompactCurrency(item.amount)}</strong>
                  </div>
                )) : (
                  <div className="accounts-list-empty premium">No invoice records available.</div>
                )}
              </div>
            </article>

            <article className="accounts-dash-list-card">
              <div className="accounts-dash-list-head">
                <h3>Top Vendors</h3>
                <button type="button" onClick={() => navigate("/employee/accounts/payments")}>View All</button>
              </div>
              <div className="accounts-dash-list-body">
                {topPaymentParties.length ? topPaymentParties.map((item) => (
                  <div key={item.name} className="accounts-dash-list-row">
                    <span>{item.name}</span>
                    <strong>{formatCompactCurrency(item.amount)}</strong>
                  </div>
                )) : (
                  <div className="accounts-list-empty premium">No payment records available.</div>
                )}
              </div>
            </article>

            <article className="accounts-dash-list-card">
              <div className="accounts-dash-list-head">
                <h3>Best Performing Modules</h3>
                <button type="button" onClick={() => navigate("/employee/accounts/reports")}>View All</button>
              </div>
              <div className="accounts-dash-list-body">
                {moduleLeaderboard.length ? moduleLeaderboard.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    className="accounts-dash-module-row"
                    onClick={() => navigate(`/employee/accounts/${item.routeKey}`)}
                  >
                    <div className={`accounts-action-icon ${toneClass[item.tone] || ""}`}>
                      <span className="material-symbols-outlined">{item.icon}</span>
                    </div>
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.count} records</span>
                    </div>
                  </button>
                )) : (
                  <div className="accounts-list-empty premium">No modules match this search.</div>
                )}
              </div>
            </article>
          </section>

          <section className="accounts-dash-ledger-grid">
            <article className="accounts-dash-ledger-card">
              <div className="accounts-dash-list-head">
                <h3>Sales Invoice Due</h3>
                <button type="button" onClick={() => navigate("/employee/accounts/invoices")}>View All</button>
              </div>
              <div className="accounts-dash-ledger-table">
                {(filteredInvoices || []).slice(0, 5).map((inv, index) => (
                  <div className="accounts-dash-ledger-row" key={inv._id || `${inv.invoiceNo}-${index}`}>
                    <span>{inv.invoiceNo || `INV-${index + 1}`}</span>
                    <span>{inv.vendor || "Unnamed party"}</span>
                    <span>{inv.status || "Pending"}</span>
                    <strong>{formatCurrency(inv.amount || invoiceAverage || 0)}</strong>
                  </div>
                ))}
                {!filteredInvoices.length && (
                  <div className="accounts-list-empty premium">No invoice records available.</div>
                )}
              </div>
            </article>

            <article className="accounts-dash-ledger-card">
              <div className="accounts-dash-list-head">
                <h3>Purchase / Payment Queue</h3>
                <button type="button" onClick={() => navigate("/employee/accounts/payments")}>View All</button>
              </div>
              <div className="accounts-dash-ledger-table">
                {(filteredPayments || []).slice(0, 5).map((payment, index) => (
                  <div className="accounts-dash-ledger-row" key={payment._id || `${payment.referenceNo}-${index}`}>
                    <span>{payment.referenceNo || `PAY-${index + 1}`}</span>
                    <span>{payment.vendor || "Scheduled payment"}</span>
                    <span>{payment.status || "Queued"}</span>
                    <strong>{formatCurrency(payment.amount || paymentAverage || 0)}</strong>
                  </div>
                ))}
                {!filteredPayments.length && (
                  <div className="accounts-list-empty premium">No payment records available.</div>
                )}
              </div>
            </article>
          </section>

          <section className="accounts-dash-footer-strip">
            <div>
              <span>Live Working Cash</span>
              <strong>{formatCompactCurrency(cashValue)}</strong>
            </div>
            <div>
              <span>Net Position</span>
              <strong>{formatCompactCurrency(netPosition)}</strong>
            </div>
            <div>
              <span>Invoice Summary</span>
              <strong>{formatCompactCurrency(invoiceSummaryTotal)}</strong>
            </div>
            <div>
              <span>Payment Summary</span>
              <strong>{formatCompactCurrency(paymentSummaryTotal)}</strong>
            </div>
          </section>
            </>
          ) : (
            <section className="accounts-dash-quicklinks-wrap">
              <div className="accounts-dash-quicklinks-grid">
                {visibleQuickLinks.map((item) => (
                  <button
                    type="button"
                    key={item.label}
                    className="accounts-dash-quicklink-card"
                    onClick={() => navigate(item.path)}
                  >
                    <span className="material-symbols-outlined">{item.icon}</span>
                    <strong>{item.label}</strong>
                  </button>
                ))}
                {visibleQuickLinks.length === 0 && (
                  <div className="accounts-empty-search">
                    No quick links match <strong>{search}</strong>.
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {!canAnyFinance && (
        <section className="accounts-empty-state">
          <span className="material-symbols-outlined">lock</span>
          <h3>No finance modules assigned</h3>
          <p>Ask an administrator to grant finance permissions for dashboard access.</p>
        </section>
      )}

      {canAnyFinance && alerts.length > 0 && (
        <section className="accounts-dash-alertline">
          {alerts.map((alert) => (
            <span key={alert}>{alert}</span>
          ))}
        </section>
      )}

      {canAnyFinance && (
        <section className="accounts-premium-actions accounts-dash-searchrow">
          <div className="accounts-search accounts-search-premium">
            <span className="material-symbols-outlined">search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search modules, invoices, payments..."
            />
          </div>
          {canReports && (
            <Button
              variant="light"
              className="accounts-btn-ghost"
              onClick={() => navigate("/employee/accounts/reports")}
            >
              Executive Report
            </Button>
          )}
          {primaryCreateRoute && (
            <Button
              className="accounts-btn-primary"
              onClick={() => navigate(primaryCreateRoute)}
            >
              New Transaction
            </Button>
          )}
        </section>
      )}
    </Container>
  );
};
